<template lang="html">
    <div
        ref="container"
        class="chart-container"
    />
</template>

<script>
import * as d3 from 'd3';

import scssVars from '@/assets/scss/_variables.scss';

export default {
    name: 'ForestDisturbancesDimensionChart',

    props: {
        chartData: {
            type: Array,
            default: () => [],
        },

        dimension: {
            type: String,
            required: true,
        },

        brushSelection: {
            type: Array,
            required: true,
        },

        binData: {
            type: Object,
            required: true,
        },

        xAxisLabel: {
            type: String,
            required: true,
        },
    },

    data: () => ({
        // svg element
        svg: null,
        // main chart g wrapper group element
        g: null,
        // svg dimensions
        width: 400,
        height: 150,
        margin: {
            top: 15, right: 0, bottom: 40, left: 50,
        },

        // 'disturbance type' dimension axis labels for type key: 1-4
        disturbanceType: ['Fire', 'Harvest', 'MPB', 'Infrastructure'],
        // 'BEC zone' dimension axis labels for bec_zone key: 1-8
        becZones: ['ESSF', 'MS', 'SBS', 'SBPS', 'BAFA', 'CWH', 'MH', 'CMA'],
        binnedData: [],
    }),

    watch: {
        brushSelection() {
            this.renderChart(this.chartData);
        },
    },

    mounted() {
        this.renderChart(this.chartData);
    },

    methods: {
        /**
         * Render histogram chart using D3 brushable bar chart pattern.
         * https://observablehq.com/@john-guerra/d3-reusable-brushable-barchart-pattern
         *
         * @param {Array} data - chart data array
         */
        renderChart(data) {
            // Set-up SVG and Wrappers:
            // crossfilter generates array of 'key' & 'value' pair objects
            const xAttr = 'key';
            const yAttr = 'value';

            // set up chart dimensions
            const { margin } = this;
            const width = this.width - margin.left - margin.right;
            const height = this.height - margin.top - margin.bottom;

            // rule of thumb is that appends should go here
            const onEnter = (enter) => {
                const svgEnter = enter.append('svg');

                const g = svgEnter.append('g').attr('class', 'gDrawing');
                g.append('g').attr('class', 'x-axis')
                    .append('text').attr('class', 'axisLabel');
                g.append('g').attr('class', 'y-axis')
                    .append('text').attr('class', 'axisLabel');
                g.append('g').attr('class', 'marks');
                g.append('g').attr('class', 'brush');

                return svgEnter;
            };
            const onUpdate = (update) => update;
            const onExit = (exit) => exit.remove();

            // select responsive div as svg container
            this.svg = d3.select(this.$refs.container)
                .selectAll('svg')
                .data([data])
                // appends, removes and reorders elements as necessary to match the data
                .join(onEnter, onUpdate, onExit)
                // responsive svg needs viewBox and preserveAspectRatio attributes and no width and height
                .attr('preserveAspectRatio', 'xMinYMin meet')
                .attr('viewBox', [0, 0, this.width, this.height])
                // responsive svg needs secondary css class to ensure absolute positioning
                .classed('forest-disturbance-chart', true);

            this.g = this.svg
                .select('g')
                .attr('transform', `translate(${margin.left}, ${margin.top})`);

            let xDomain = [];
            let yDomain = [];
            // run d3 bin on the data and set specific domains
            // for the x and y axes that will work with the binned data arrays
            if (this.binData.useBinnedData) {
                const bins = d3.bin()
                    .value((d) => d.key)
                    .thresholds(this.binData.binThreshold);

                // remove any placeholder -9999 data before binning
                const filteredData = data.filter((dataItem) => dataItem.key >= -5000);

                this.binnedData = bins(filteredData);

                xDomain = this.binnedData.map((d) => d.x0);
                xDomain.push(this.binnedData[this.binnedData.length - 1].x1);

                const sums = this.binnedData.map((bin) => this.sumBinnedData(bin));

                yDomain = d3.max(sums);
            } else {
                // set domains for x and y axes for non binned data
                xDomain = data.map((d) => {
                    // type dimension
                    for (let i = 0; i < this.disturbanceType.length; i += 1) {
                        if (d[xAttr] === i + 1 && this.dimension === 'type') {
                            return this.disturbanceType[i];
                        }
                    }
                    // biogeoclimatic zone dimension
                    for (let i = 0; i < this.becZones.length; i += 1) {
                        if (d[xAttr] === i + 1 && this.dimension === 'bec_zone') {
                            return this.becZones[i];
                        }
                    }
                    // other dimensions
                    return d[xAttr];
                });
                yDomain = d3.max(data, (d) => d[yAttr]);
            }

            // Initialize Scales & Axes:
            // initialize x-scale
            const xScale = d3.scaleBand().padding(0.2)
                .domain(xDomain)
                .range([0, width]);

            // initialize y-scale
            const yScale = d3.scaleLinear()
                .domain([0, yDomain])
                .range([height, 0])
                .nice();

            // initialize transition
            const t = this.g.transition().duration(750);

            // customize x-axis ticks intervals
            const xScaleDomain = xScale.domain().filter((value) => !(value < 0)); // remove invalid values (e.g. -9999)
            let xAxisTickValues;
            if (this.dimension === 'aspect') {
                xAxisTickValues = d3.range(d3.min(xScaleDomain), d3.max(xScaleDomain) + 1, 40);
            } else if (this.dimension === 'elevation' || xScaleDomain.length >= 100) {
                // returns an array containing an arithmetic progression
                xAxisTickValues = d3.range(d3.min(xScaleDomain), d3.max(xScaleDomain) + 1, 100);
            } else if (xScaleDomain.length >= 20) {
                xAxisTickValues = d3.range(d3.min(xScaleDomain), d3.max(xScaleDomain) + 1, 5);
            } else {
                xAxisTickValues = xScaleDomain;
            }
            // remove generated x-axis tick values that do not have bounded domain data
            xAxisTickValues = xAxisTickValues.filter((value) => xScaleDomain.includes(value));

            // initialize x-axis
            this.g.select('.x-axis')
                .style('font-size', 12)
                .attr('transform', `translate(0, ${height})`)
                .transition(t)
                .call(d3.axisBottom(xScale).tickValues(xAxisTickValues));

            this.g.select('.x-axis')
                .select('.axisLabel')
                .style('fill', '#3c3c3c')
                .style('font-size', 14)
                .attr('transform', `translate(${width / 2}, 35)`)
                .text(this.xAxisLabel);

            // customize y-axis ticks intervals
            const yScaleDomain = yScale.domain();
            // 1 disturbance point count is equivalent to 0.9 km² disturbance area coverage (eg. [0, 7000] => [0, 6300])
            const adjustedYScaleDomain = yScaleDomain.map((input) => input * 0.9);
            let yAxisTickValues;
            if (adjustedYScaleDomain[1] >= 10000) {
                // returns an array containing an arithmetic progression
                yAxisTickValues = d3.range(d3.min(adjustedYScaleDomain), d3.max(adjustedYScaleDomain) + 1, 5000);
            } else if (adjustedYScaleDomain[1] >= 5000) {
                yAxisTickValues = d3.range(d3.min(adjustedYScaleDomain), d3.max(adjustedYScaleDomain) + 1, 2000);
            } else if (adjustedYScaleDomain[1] >= 1000) {
                yAxisTickValues = d3.range(d3.min(adjustedYScaleDomain), d3.max(adjustedYScaleDomain) + 1, 500);
            } else if (adjustedYScaleDomain[1] >= 500) {
                yAxisTickValues = d3.range(d3.min(adjustedYScaleDomain), d3.max(adjustedYScaleDomain) + 1, 100);
            } else if (adjustedYScaleDomain[1] >= 100) {
                yAxisTickValues = d3.range(d3.min(adjustedYScaleDomain), d3.max(adjustedYScaleDomain) + 1, 50);
            } else if (adjustedYScaleDomain[1] >= 10) {
                yAxisTickValues = d3.range(d3.min(adjustedYScaleDomain), d3.max(adjustedYScaleDomain) + 1, 10);
            } else if (adjustedYScaleDomain[1] >= 1) {
                yAxisTickValues = d3.range(d3.min(adjustedYScaleDomain), d3.max(adjustedYScaleDomain) + 1, 2);
            } else {
                yAxisTickValues = d3.range(d3.min(adjustedYScaleDomain), d3.max(adjustedYScaleDomain) + 0.1, 0.2);
            }

            // initialize y-axis
            this.g.select('.y-axis')
                .style('font-size', 12)
                .transition(t)
                .call(d3.axisLeft(yScale).tickValues(yAxisTickValues));

            this.g.select('.y-axis')
                .select('.axisLabel')
                .style('fill', '#3c3c3c')
                .style('font-size', 14)
                .attr('transform', 'translate(-5, -5)')
                .text('km²');

            // Draw Bar Chart:
            // update chart bars
            const moveItems = (item) => {
                // handle updating items for binned data charts
                // filter out items in binned array with values of 0 so
                // the correct count will display for the bin in the chart
                if (this.binData.useBinnedData) {
                    item
                        .attr('x', (d) => xScale(d.x0))
                        .attr('width', (d) => (xScale(d.x1) - xScale(d.x0)) - 3) // leave extra space between bars
                        .attr('y', (d) => yScale(this.sumBinnedData(d)))
                        .attr('height', (d) => height - yScale(this.sumBinnedData(d)));
                } else {
                    // handle updating items for non binned items
                    item
                        .attr('x', (d) => {
                            // type dimension
                            for (let i = 0; i < this.disturbanceType.length; i += 1) {
                                if (d[xAttr] === i + 1 && this.dimension === 'type') {
                                    return xScale(this.disturbanceType[i]);
                                }
                            }
                            // biogeoclimatic zone dimension
                            for (let i = 0; i < this.becZones.length; i += 1) {
                                if (d[xAttr] === i + 1 && this.dimension === 'bec_zone') {
                                    return xScale(this.becZones[i]);
                                }
                            }
                            // other dimensions
                            return xScale(d[xAttr]);
                        })
                        .attr('y', (d) => yScale(d[yAttr]))
                        // require inverting range for right side up rects
                        .attr('height', (d) => height - yScale(d[yAttr]))
                        .attr('width', xScale.bandwidth());
                }
            };

            if (this.binData.useBinnedData) {
                // update subchart g marks group element for binned data
                // filter out items in binned array with values of 0 so
                // the correct count will display for the bin in the chart
                this.g.select('.marks')
                    .selectAll('.item')
                    .data(this.binnedData)
                    .join(
                        (enter) => enter.append('rect').attr('class', 'item').call(moveItems),
                        (update) => update.call((u) => u.transition(t).call(moveItems)),
                    )
                    .attr('x', (d) => xScale(d.x0))
                    .attr('data-xval', (d) => d.x0)
                    .attr('width', (d) => (xScale(d.x1) - xScale(d.x0)) - 3) // leave extra space between bars
                    .attr('y', (d) => yScale(this.sumBinnedData(d)))
                    .attr('height', (d) => height - yScale(this.sumBinnedData(d)))
                    .style('fill', 'rgba(70, 130, 180, 1)');
            } else {
                // update subchart g marks group element for non binned items
                this.g.select('.marks')
                    .selectAll('.item')
                    .data(data)
                    .join(
                        (enter) => enter.append('rect').attr('class', 'item').call(moveItems),
                        (update) => update.call((u) => u.transition(t).call(moveItems)),
                    )
                    .style('fill', (d) => {
                        // for main 'type' dimension control
                        if (d[xAttr] === 1 && this.dimension === 'type') {
                            return scssVars.colorFireDisturbance;
                        }
                        if (d[xAttr] === 2 && this.dimension === 'type') {
                            return scssVars.colorHarvestDisturbance;
                        }
                        if (d[xAttr] === 3 && this.dimension === 'type') {
                            return scssVars.colorMountainPineBeetleDisturbance;
                        }
                        if (d[xAttr] === 4 && this.dimension === 'type') {
                            return scssVars.colorInfrastructureDisturbance;
                        }
                        // for the rest dimension controls
                        return 'rgba(70, 130, 180, 1)';
                    });
            }

            // reference component for brush function
            const thisComponent = this;

            /**
             * Create Brush Function:
             *
             * @param {object} event - brush event
             */
            function brushed(event) {
                const { selection } = event;
                if (!event.sourceEvent) return;

                if (selection) {
                    const [x0, x1] = selection;

                    let isSelected;
                    // main disturbances type dimension control
                    if (thisComponent.dimension === 'type') {
                        isSelected = (d) => (
                            xScale(thisComponent.disturbanceType[d[xAttr] - 1]) + xScale.bandwidth() >= x0)
                            && (xScale(thisComponent.disturbanceType[d[xAttr] - 1]) <= x1);
                    // biogeoclimatic zone dimension control
                    } else if (thisComponent.dimension === 'bec_zone') {
                        isSelected = (d) => (
                            xScale(thisComponent.becZones[d[xAttr] - 1]) + xScale.bandwidth() >= x0)
                            && (xScale(thisComponent.becZones[d[xAttr] - 1]) <= x1);
                    // other dimension controls
                    } else {
                        isSelected = (d) => (xScale(d[xAttr]) + xScale.bandwidth() >= x0) && (xScale(d[xAttr]) <= x1);
                    }

                    let filteredData = d3.select(thisComponent.$refs.container.querySelector('svg'))
                        .selectAll('.item')
                        .style('fill', 'rgba(204, 204, 204, 1)')
                        .filter(isSelected)
                        .style('fill', (d) => {
                            // main disturbances type dimension control
                            if (d[xAttr] === 1 && this.dimension === 'type') {
                                return scssVars.colorFireDisturbance;
                            }
                            if (d[xAttr] === 2 && this.dimension === 'type') {
                                return scssVars.colorHarvestDisturbance;
                            }
                            if (d[xAttr] === 3 && this.dimension === 'type') {
                                return scssVars.colorMountainPineBeetleDisturbance;
                            }
                            if (d[xAttr] === 4 && this.dimension === 'type') {
                                return scssVars.colorInfrastructureDisturbance;
                            }
                            // other dimension controls
                            return 'rgba(70, 130, 180, 1)';
                        })
                        .data();

                    // for binned data filter the data between(and including) the selected bins
                    if (thisComponent.binData.useBinnedData) {
                        filteredData = thisComponent.chartData
                            // remove any placeholder -9999 data before filtering
                            .filter((dataItem) => dataItem.key >= -5000)
                            .filter(({ key }) => {
                                // find the bucket dataItem is in
                                const containingBin = thisComponent.binnedData
                                    .find((bin) => key >= bin.x0 && key <= bin.x1);

                                // find the x0 and x1 pixel coordinates of the bucket
                                const { x0: binx0, x1: binx1 } = containingBin;

                                // return true if the bucket overlaps with the selection
                                return xScale(binx1) >= x0 && xScale(binx0) <= x1;
                            });
                    }

                    thisComponent.$emit('changeFilters', [x0, x1], thisComponent.dimension, filteredData);
                } else {
                    d3.select(thisComponent.$refs.container.querySelector('svg'))
                        .selectAll('.item')
                        .style('fill', (d) => {
                            // main disturbances type dimension control
                            if (d[xAttr] === 1 && this.dimension === 'type') {
                                return scssVars.colorFireDisturbance;
                            }
                            if (d[xAttr] === 2 && this.dimension === 'type') {
                                return scssVars.colorHarvestDisturbance;
                            }
                            if (d[xAttr] === 3 && this.dimension === 'type') {
                                return scssVars.colorMountainPineBeetleDisturbance;
                            }
                            if (d[xAttr] === 4 && this.dimension === 'type') {
                                return scssVars.colorInfrastructureDisturbance;
                            }
                            // other dimension controls
                            return 'rgba(70, 130, 180, 1)';
                        });
                    const allData = thisComponent.chartData;
                    thisComponent.$emit('changeFilters', [0, width], thisComponent.dimension, allData);
                }
            }

            // create one-dimensional brush along x-dimension
            const brush = d3.brushX()
                // specify extent:[x0, y0] top-left corner, [x1, y1] bottom-right corner
                .extent([[0, 0], [width, height]])
                // start - at the start of a brush gesture (e.g. mousedown)
                // brush - when brush moves (e.g. mousemove)
                // end - at the end of brush gesture (e.g. mouseup)
                .on('start brush end', brushed);
            this.g.select('g.brush').call(brush);
        },

        /**
         * Sum binned array of data
         *
         * @param {Array} data - array of data to be summed
         * @returns {number} - sum of the binned data array
         */
        sumBinnedData(data) {
            const filteredData = data.filter((binItem) => binItem.value);
            return filteredData.reduce((partialSum, a) => partialSum + a.value, 0);
        },
    },
};
</script>

<style lang="scss">
.chart-container {
    .forest-disturbance-chart {
        width: 100%;
        height: 12vh;
    }
}
</style>
